import { useSignal } from '@preact/signals-react'
import { useSignals } from '@preact/signals-react/runtime'
import { useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Tag } from 'primereact/tag'
import { ToggleButton } from 'primereact/togglebutton'
import { UniqueComponentId } from 'primereact/utils'
import React, { useEffect, useMemo, useRef } from 'react'
import PhoneInput from 'react-phone-input-2'
import { ReactComponent as NotifyImg } from '../../assets/images/undraw_new_notifications.svg'
import { SystemRole } from '../../Constants'
import { useAppConfig } from '../../context/app/AppContext'
import { useUserHospital } from '../../context/hospital/UserHospitalContext'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import { QueryKeyTypes } from '../../query-manager/QueryKeyTypes'
import { useAuth } from '../authentication/AuthProvider'
import { getClientId } from '../authentication/AuthUtils'
import { component } from '../form/Constant'
import { severityNotification } from '../notification/NotificationConstant'
import { showNotification } from '../notification/NotificationService'
import { isArrayAndNotEmpty } from '../observations/Utils'
import { showOverlayTooltip } from '../overlay-tooltip/OverlayTooltipService'
import StyledHospitalOptions from '../styled-component/styled-options/StyledHospitalOptions'
import { convertKeyToLabel } from '../table-utils/TableUtils'
import {
    getSystemConfigurationEventKey,
    onSystemConfigurationChangeRemoveListener,
    systemConfigurationEvent,
    systemConfigurationTypes,
} from './broadcast/GenericLogEvent'
import { timeOptionKeys } from './Constants'
import {
    useAdditionalNotifyMethod,
    usePageAdditionalNotifyMethod,
} from './query-keys/AdditionNotifyMethodQueryManager'
import StyledNotifyMethodsForm from './StyledNotifyMethodsForm'
const InitialPageable = {
    pageRows: 9,
    pageNumber: 0,
    sortField: 'id',
    sortOrder: -1,
}
const InitialFilterRequest = {}
const StyledAdditionalNotifyMethod = () => {
    useSignals()
    const {user} = useAuth()
    const {currentHospital} = useUserHospital()
    const {timezoneId, userRole} = useAppConfig()
    const notifyMethodsFormRef = useRef(null)
    const filterRequest = useMemo(() => ({...InitialFilterRequest}), [])

    const queryClient = useQueryClient()
    const {mutateAsync: createOrUpdateAdditionalNotifyMethod} =
        useAdditionalNotifyMethod(QueryCRUDTypes.update, user?.id)

    const pageable = useSignal({...InitialPageable})
    const filterData = useSignal({
        ...filterRequest,
        hospitalId: userRole === SystemRole.ADMIN ? null : currentHospital.id,
    })

    const hospitalId = useMemo(() => {
        return userRole === SystemRole.ADMIN
            ? filterData.value.hospitalId
            : currentHospital.id
    }, [currentHospital.id, filterData.value.hospitalId, userRole])
    const {data, isFetching} = usePageAdditionalNotifyMethod(
        user?.id,
        hospitalId,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        {
            ...filterData.value,
            hospitalId: hospitalId,
            // clientId : user?.clientId
        },
        !!user?.id && !!currentHospital?.id,
    )
    const dataList = useMemo(() => {
        let holderData = isArrayAndNotEmpty(data?.content)
            ? [...data?.content]
            : []
        return holderData
    }, [data, isFetching])

    const totalElements = useMemo(() => {
        return data?.totalElements || 0
    }, [data?.totalElements])

    //#region Event Listener on State Changes
    useEffect(() => {
        systemConfigurationEvent.on(
            `${getSystemConfigurationEventKey(
                systemConfigurationTypes.ADDITIONAL_METHOD_CHANGE,
            )}`,
            newData => {
                if (!newData.clientId || newData.clientId !== getClientId()) {
                    return
                }
                if (
                    !newData.hospitalId ||
                    newData.hospitalId !== currentHospital?.id
                ) {
                    return
                }
                if (!newData?.userId || newData?.userId === user?.id) {
                    return
                }
                if (!newData || !newData?.id) {
                    return
                }

                const {additionalNotifyMethodKeys} = QueryKeyManager(
                    QueryKeyTypes.additionalNotifyMethod,
                    user?.id,
                )
                queryClient.invalidateQueries({
                    queryKey: additionalNotifyMethodKeys.pattern(),
                })
                showNotification(
                    severityNotification.success,
                    'The Additional Notify Method has just been updated.',
                )

                if (notifyMethodsFormRef?.current) {
                    notifyMethodsFormRef.current.hideForm()
                }
            },
        )

        return () => {
            onSystemConfigurationChangeRemoveListener(
                systemConfigurationTypes.ADDITIONAL_METHOD_CHANGE,
            )
        }
    }, [])
    //#endregion

    const sortByDay = days => {
        if (!isArrayAndNotEmpty(days)) {
            return []
        }
        const weekDays = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ]
        const sortedDays = days.sort((a, b) => {
            return weekDays.indexOf(a) - weekDays.indexOf(b)
        })
        const today = moment().tz(timezoneId).format('dddd').toLowerCase()

        return sortedDays.map(item => {
            return {day: item, today: item === today}
        })
    }
    const handleOnToggle = async (item, value) => {
        const toPostData = {
            ...item,
            hospitalId: currentHospital?.id,
            active: value,
        }
        const res = await createOrUpdateAdditionalNotifyMethod(toPostData)
        if (res?.id) {
            const {additionalNotifyMethodKeys} = QueryKeyManager(
                QueryKeyTypes.additionalNotifyMethod,
                user?.id,
            )
            queryClient.invalidateQueries({
                queryKey: additionalNotifyMethodKeys.pattern(),
            })
        }
    }
    const handleLoadMore = () => {
        let pageRows = pageable.value.pageRows
        if (pageRows < totalElements) {
            pageable.value = {
                ...pageable.value,
                pageRows: pageRows + 9,
            }
        }
    }
    return (
        <div>
            <div className="flex justify-content-between align-items-center">
                <div className="">
                    <h3 className="pt-2 mb-0 font-bold text-xl md:text-2xl ">
                        Additional Notify Methods
                    </h3>
                    <p>
                        Add additional methods to send notifications through
                        various channels such as phone number, SMS, etc{' '}
                    </p>
                </div>
                <div>
                    {filterData.value?.hospitalId && (
                        <Button
                            className="h-3rem col-12 px-3"
                            icon="pi pi-plus"
                            label="ADD NEW"
                            onClick={() => {
                                notifyMethodsFormRef.current.showForm()
                            }}
                        />
                    )}
                </div>
            </div>
            {userRole === SystemRole.ADMIN && (
                <div className="py-2">
                    <StyledHospitalOptions
                        optionType={component.dropdown}
                        onChange={hospitalId =>
                            (filterData.value = {
                                ...filterData.value,
                                hospitalId: hospitalId,
                            })
                        }
                    />
                </div>
            )}

            {dataList && isArrayAndNotEmpty(dataList) ? (
                <div className="grid my-2 ">
                    {dataList?.map((item, idx) => (
                        <div
                            key={`${UniqueComponentId()}`}
                            className=" col-12 md:col-6 lg:col-4 p-1 relative">
                            <div
                                className={`flex flex-column justify-content-between border-1 ${
                                    item.activeAndValid
                                        ? 'border-teal-500'
                                        : 'opacity-70 border-gray-300 hover:border-gray-400'
                                }  border-round-xl  h-full`}>
                                <div className="absolute">
                                    <Tag
                                        className="px-3"
                                        severity={
                                            !item.active
                                                ? 'danger'
                                                : item.activeAndValid
                                                ? 'success'
                                                : 'warning'
                                        }
                                        style={{
                                            borderTopRightRadius: 0,
                                            borderBottomLeftRadius: 0,
                                        }}
                                        value={item.status}
                                    />
                                </div>
                                <div className="p-2">
                                    {' '}
                                    <div className="flex justify-content-between ">
                                        <div className="pt-4">
                                            <div className="flex gap-2">
                                                {' '}
                                                <h3 className="mb-1">
                                                    {idx + 1}. {item.name}
                                                </h3>
                                            </div>

                                            <p>{item.description}</p>
                                        </div>
                                        <div className="">
                                            <ToggleButton
                                                className=" mr-2"
                                                onLabel="On"
                                                offLabel="Off"
                                                onIcon="pi pi-power-off"
                                                offIcon="pi pi-caret-right"
                                                checked={item.active}
                                                onChange={e =>
                                                    handleOnToggle(
                                                        item,
                                                        e.value,
                                                    )
                                                }
                                            />
                                            <Button
                                                className="h-3rem col-12 m-0 bg-teal-50 border-teal-50 text-teal-600 font-bold hover:text-teal-700"
                                                icon="pi pi-cog"
                                                onClick={() => {
                                                    notifyMethodsFormRef.current.showForm(
                                                        item,
                                                    )
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex align-items-center gap-2 mb-2">
                                        {item.time ===
                                        timeOptionKeys.customTime ? (
                                            <React.Fragment>
                                                <Button
                                                    className={`col h-3rem bg-gray-100 border-gray-100 text-darkcyan hover:text-teal-600 opacity-90`}
                                                    disabled={true}
                                                    icon="pi pi-clock"
                                                    label={item.startTime}
                                                />
                                                <div>
                                                    <i className="pi pi-chevron-right text-sm" />{' '}
                                                </div>
                                                <Button
                                                    className={`col h-3rem bg-gray-100 border-gray-100 text-darkcyan hover:text-teal-600 opacity-90`}
                                                    disabled={true}
                                                    icon="pi pi-clock"
                                                    label={item.endTime}
                                                />
                                            </React.Fragment>
                                        ) : (
                                            <Button
                                                className={`col h-3rem bg-gray-100 border-gray-100 text-darkcyan hover:text-teal-600 opacity-90`}
                                                disabled={true}
                                                icon="pi pi-clock"
                                                label={convertKeyToLabel(
                                                    timeOptionKeys.allDay,
                                                )}
                                            />
                                        )}
                                    </div>
                                    <div className="grid gap-1">
                                        {isArrayAndNotEmpty(item.days) &&
                                            sortByDay(item.days).map(
                                                (dayItem, dIdx) => (
                                                    <div
                                                        className="col p-0"
                                                        key={`${UniqueComponentId()}`}>
                                                        {' '}
                                                        <Button
                                                            className={`w-full  ${
                                                                dayItem.day ===
                                                                item.today
                                                                    ? 'bg-gray-100 border-gray-100 text-darkcyan hover:text-teal-600'
                                                                    : 'text-700 border-gray-100 bg-white'
                                                            }  opacity-90`}
                                                            icon={
                                                                dayItem.day ===
                                                                    item.today &&
                                                                'pi pi-calendar'
                                                            }
                                                            disabled={true}
                                                            label={convertKeyToLabel(
                                                                dayItem.day.slice(
                                                                    0,
                                                                    3,
                                                                ),
                                                            )}
                                                        />
                                                    </div>
                                                ),
                                            )}
                                    </div>
                                </div>

                                <div className="grid p-2">
                                    <div className="col-6 pr-1 pl-0">
                                        <Button
                                            className={`${
                                                item.totalPhoneActive < 1 ||
                                                item.phone.length < 1
                                                    ? 'bg-gray-100 text-600 border-gray-100'
                                                    : 'bg-teal-50 border-teal-50 text-teal-600 hover:text-teal-700'
                                            } w-full h-full text-sm`}
                                            label={
                                                item.notifyMethodType.includes(
                                                    'sms',
                                                )
                                                    ? `${
                                                          item.totalPhoneActive ||
                                                          0
                                                      }/${
                                                          item.phone.length || 0
                                                      } phone
                                            numbers are active`
                                                    : 'The method has not been selected.'
                                            }
                                            icon={'pi pi-envelope'}
                                            onClick={e =>
                                                item.phone.length > 0 &&
                                                showOverlayTooltip(
                                                    e,
                                                    <div
                                                        className="overflow-scroll"
                                                        style={{
                                                            maxHeight: '30rem',
                                                        }}>
                                                        {item.phone.map(
                                                            (
                                                                phoneItem,
                                                                pIdx,
                                                            ) => (
                                                                <div
                                                                    key={`${UniqueComponentId()}`}
                                                                    className="flex mb-1 relative">
                                                                    <PhoneInput
                                                                        id={`phone-input-${idx}-${pIdx}`}
                                                                        className={`p-0 font-semibold cursor-auto`}
                                                                        inputClass={
                                                                            'font-semibold '
                                                                        }
                                                                        country={
                                                                            'gb'
                                                                        }
                                                                        preferredCountries={[
                                                                            'gb',
                                                                            'us',
                                                                            'ca',
                                                                            'vn',
                                                                            'bg',
                                                                        ]}
                                                                        inputStyle={{
                                                                            minHeight:
                                                                                '40px',
                                                                        }}
                                                                        disabled={
                                                                            true
                                                                        }
                                                                        alwaysDefaultMask={
                                                                            true
                                                                        }
                                                                        value={
                                                                            phoneItem.phone
                                                                        }
                                                                    />
                                                                    <Tag
                                                                        className={`absolute right-0 ${
                                                                            !phoneItem.active
                                                                                ? 'bg-gray-500'
                                                                                : ''
                                                                        }`}
                                                                        value={
                                                                            phoneItem.active
                                                                                ? 'Active'
                                                                                : 'Inactive'
                                                                        }
                                                                        style={{
                                                                            borderTopLeftRadius: 0,
                                                                            borderBottomRightRadius: 0,
                                                                        }}
                                                                    />
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>,
                                                )
                                            }
                                            // onMouseLeave={e =>
                                            //     hideOverlayTooltip(e)
                                            // }
                                        />
                                    </div>
                                    <div className="col-6 pr-0  pl-1">
                                        <Button
                                            className={`${
                                                item.totalEmailActive < 1 ||
                                                item.email.length < 1
                                                    ? 'bg-gray-100 text-600 border-gray-100'
                                                    : 'bg-teal-50 border-teal-50 text-teal-600 hover:text-teal-700'
                                            } w-full h-full text-sm`}
                                            label={
                                                item.notifyMethodType.includes(
                                                    'email',
                                                )
                                                    ? `${
                                                          item.totalEmailActive ||
                                                          0
                                                      }/${
                                                          item.email.length || 0
                                                      } email${
                                                          item.email.length < 2
                                                              ? 's'
                                                              : ''
                                                      } are active`
                                                    : 'The method has not been selected.'
                                            }
                                            icon={'pi pi-at'}
                                            onClick={e =>
                                                item.email.length > 0 &&
                                                showOverlayTooltip(
                                                    e,
                                                    <div
                                                        className="overflow-scroll"
                                                        style={{
                                                            maxHeight: '30rem',
                                                        }}>
                                                        {item.email?.map(
                                                            (
                                                                emailItem,
                                                                eIdx,
                                                            ) => (
                                                                <div
                                                                    key={`${UniqueComponentId()}`}
                                                                    className="flex mb-1 relative">
                                                                    <InputText
                                                                        key={`${UniqueComponentId()}`}
                                                                        className="col h-3rem font-semibold opacity-100"
                                                                        defaultValue={
                                                                            emailItem.email
                                                                        }
                                                                        disabled
                                                                    />
                                                                    <Tag
                                                                        className={`absolute right-0 ${
                                                                            !emailItem.active
                                                                                ? 'bg-gray-500'
                                                                                : ''
                                                                        }`}
                                                                        value={
                                                                            emailItem.active
                                                                                ? 'Active'
                                                                                : 'Inactive'
                                                                        }
                                                                        style={{
                                                                            borderTopLeftRadius: 0,
                                                                            borderBottomRightRadius: 0,
                                                                        }}
                                                                    />
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>,
                                                )
                                            }
                                            // onMouseLeave={e =>
                                            //     hideOverlayTooltip(e)
                                            // }
                                        />
                                    </div>
                                    <i></i>
                                </div>
                            </div>{' '}
                        </div>
                    ))}
                    <div className="col-12">
                        {' '}
                        <Button
                            className="w-full"
                            icon={`pi pi-spin ${
                                isFetching ? 'pi-spinner' : ''
                            }`}
                            label={`Show more (Showing ${
                                dataList?.length || 0
                            } of ${totalElements || 0} total record(s))`}
                            onClick={() => handleLoadMore()}
                        />
                    </div>
                </div>
            ) : (
                <div className="flex justify-content-center py-6">
                    <div>
                        {' '}
                        <NotifyImg className="h-17rem" />
                        <h5 className="mb-1 text-center">
                            There is no notify method added.
                        </h5>
                    </div>
                </div>
            )}

            <StyledNotifyMethodsForm
                ref={notifyMethodsFormRef}
                hospitalId={hospitalId}
            />
        </div>
    )
}

export default StyledAdditionalNotifyMethod
